import { useEffect } from 'preact/hooks';

export interface HotkeyOptions {
  /** Allow the hotkey to fire when the user is typing in an input/textarea/contenteditable. Defaults to false. */
  allowInEditable?: boolean;
  /** Prevent the default browser action when matched. Defaults to true. */
  preventDefault?: boolean;
  /** Enable/disable the listener at runtime. */
  enabled?: boolean;
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isEditable(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return false;
}

function normalizeKey(keys: string): string[] {
  return keys
    .toLowerCase()
    .split('+')
    .map((k) => k.trim())
    .map((k) => (k === 'cmd' || k === 'command' ? 'meta' : k));
}

function matches(e: KeyboardEvent, tokens: string[]): boolean {
  // `e.key` puede ser undefined en eventos sintéticos (algunos IMEs, dead
  // keys, browser quirks) — fallback a string vacío para no romper el
  // listener global y dejar la app sin responder a teclas.
  const key = (e.key ?? '').toLowerCase();
  const hasMod = {
    meta: tokens.includes('meta'),
    ctrl: tokens.includes('ctrl'),
    shift: tokens.includes('shift'),
    alt: tokens.includes('alt'),
  };
  const plainKey = tokens.find((t) => !['meta', 'ctrl', 'shift', 'alt', 'mod'].includes(t));
  if (!plainKey) return false;
  const modOk =
    (hasMod.meta ? e.metaKey : !e.metaKey) &&
    (hasMod.ctrl ? e.ctrlKey : !e.ctrlKey) &&
    (hasMod.shift ? e.shiftKey : !e.shiftKey) &&
    (hasMod.alt ? e.altKey : !e.altKey);
  // 'mod' token → accept either meta (mac) or ctrl (others).
  const wantsMod = tokens.includes('mod');
  const modOrMetaCtrl = wantsMod ? e.metaKey || e.ctrlKey : modOk;
  return modOrMetaCtrl && key === plainKey;
}

/**
 * Register a keyboard shortcut. Supports "mod+k", "cmd+k", "ctrl+shift+p", "Escape", "?", etc.
 * Examples:
 *   useHotkey('mod+k', () => openPalette());
 *   useHotkey('Escape', () => close(), { allowInEditable: true });
 */
export function useHotkey(
  keys: string | string[],
  handler: (e: KeyboardEvent) => void,
  opts: HotkeyOptions = {},
): void {
  const { allowInEditable = false, preventDefault = true, enabled = true } = opts;

  useEffect(() => {
    if (!enabled) return;
    const combos = (Array.isArray(keys) ? keys : [keys]).map(normalizeKey);

    const onKeyDown = (e: KeyboardEvent) => {
      if (!allowInEditable && isEditable(e.target)) return;
      for (const tokens of combos) {
        if (matches(e, tokens)) {
          if (preventDefault) e.preventDefault();
          handler(e);
          return;
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keys, handler, allowInEditable, preventDefault, enabled]);
}
